import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import { partyDisplayName } from '../../../Utils/DisplayNameUtil';
import { useAppSelector } from '../../../Redux/hooks';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import { PAYMENT_METHODS, type ReceiptModel, type UnreportedSummaryModel } from '../../../Models/ReceiptModel';
import { ReceiptService, type ReceiptQuery } from '../../../Services/ReceiptService';
import { formatDate } from '../../../Utils/DateUtil';
import { amountValue } from '../../../Utils/MoneyUtil';
import { exportCsv, exportJson, exportStamp, exportXls, type ExportRow } from '../../../Utils/ExportUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { useToast } from '../../SharedArea/Toast/ToastProvider';
import { PremiumGate } from '../Premium/PremiumGate';
import { ReceiptDocument } from './ReceiptDocument';
import { ReceiptEditor } from './ReceiptEditor';
import './Receipts.css';
import { PremiumSubnav } from '../Premium/PremiumSubnav';

const METHOD_KEYS = {
  cash: 'methodCash',
  bank_transfer: 'methodBankTransfer',
  credit_card: 'methodCreditCard',
  bit: 'methodBit',
  paybox: 'methodPaybox',
  check: 'methodCheck',
  other: 'methodOther'
} as const;

interface Filters {
  q: string;
  status: string;
  from: string;
  to: string;
  method: string;
  minAmount: string;
  maxAmount: string;
}

const NO_FILTERS: Filters = { q: '', status: '', from: '', to: '', method: '', minAmount: '', maxAmount: '' };

/**
 * The receipt list, plus the one thing a tutor actually comes here to do:
 * turn paid lessons that have no receipt yet into a receipt.
 *
 * Unreported lessons lead, because that is the outstanding work. A tutor with
 * nothing unreported is up to date and does not need to read the list.
 */
export function Receipts() {
  return (
    <PremiumGate feature={PremiumFeature.Receipts}>
      <ReceiptsInner />
    </PremiumGate>
  );
}

function ReceiptsInner() {
  const { tr, language } = useTranslation();
  const displayName = useDisplayName();
  const students = useAppSelector(state => state.students.items);
  const toast = useToast();
  const [receipts, setReceipts] = useState<ReceiptModel[]>([]);
  const [unreported, setUnreported] = useState<UnreportedSummaryModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ReceiptModel | null>(null);
  const [cancelling, setCancelling] = useState<ReceiptModel | null>(null);
  const [deleting, setDeleting] = useState<ReceiptModel | null>(null);
  const [editing, setEditing] = useState<{ receipt: ReceiptModel | null; duplicate: boolean } | null>(null);
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [bulkIssuing, setBulkIssuing] = useState(false);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);

  // Text, status and the date range are the server's, because it holds fields
  // the list rows do not carry. Method and the amount range are filtered here,
  // where the values are already in hand.
  const query: ReceiptQuery = useMemo(() => ({
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {})
  }), [filters.q, filters.status, filters.from, filters.to]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, pending] = await Promise.all([
        ReceiptService.list(query),
        ReceiptService.unreported()
      ]);
      setReceipts(list);
      setUnreported(pending);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void reload(); }, [reload]);

  const visible = useMemo(() => {
    const min = filters.minAmount ? Number(filters.minAmount) : null;
    const max = filters.maxAmount ? Number(filters.maxAmount) : null;
    return receipts.filter(receipt => {
      if (filters.method && receipt.paymentMethod !== filters.method) return false;
      const value = amountValue(receipt.amount);
      if (min !== null && value < min) return false;
      if (max !== null && value > max) return false;
      return true;
    });
  }, [receipts, filters.method, filters.minAmount, filters.maxAmount]);

  const filtering = Object.values(filters).some(Boolean);

  function set<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters(current => ({ ...current, [key]: value }));
  }

  async function consolidate(student: string) {
    try {
      const draft = await ReceiptService.consolidate(student);
      await reload();
      setOpen(draft);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function bulkIssue() {
    setBulkIssuing(true);
    try {
      const result = await ReceiptService.bulkIssue();
      setBulkConfirming(false);
      await reload();
      toast({ message: tr('bulkIssueDone', result.count) });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBulkIssuing(false);
    }
  }

  // What is on screen, not the whole ledger: an export is a copy of the view
  // the tutor built with the filters, or the filters were pointless.
  function rowsForExport(): ExportRow[] {
    return visible.map(receipt => ({
      [tr('receiptNumber')]: receipt.receiptNumber ?? tr('receiptDraft'),
      [tr('status')]: tr(receipt.status === 'issued' ? 'receiptIssued' : receipt.status === 'cancelled' ? 'receiptCancelledShort' : 'receiptDraft'),
      [tr('customerName')]: receipt.customerName ?? receipt.student ?? '',
      [tr('amount')]: amountValue(receipt.amount),
      [tr('issueDate')]: receipt.issueDate ?? '',
      [tr('paymentDate')]: receipt.paymentDate ?? '',
      [tr('paymentMethod')]: receipt.paymentMethod ? tr(METHOD_KEYS[receipt.paymentMethod as keyof typeof METHOD_KEYS] ?? 'methodOther') : '',
      [tr('notes')]: receipt.description ?? ''
    }));
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorState code={error} onRetry={() => void reload()} />;

  return (
    <section className="receipts">
      <PremiumSubnav />
      <header className="receipts-header">
        <h1 className="screen-title">{tr('navReceipts')}</h1>
        <button type="button" className="primary-button" onClick={() => setEditing({ receipt: null, duplicate: false })}>
          {tr('newReceipt')}
        </button>
      </header>

      {unreported && unreported.count > 0 && (
        <section className="receipts-pending">
          <h2>
            {tr('unreportedTitle')} <MoneyText amount={unreported.total} />
          </h2>
          <button type="button" className="ghost-button" onClick={() => setBulkConfirming(true)}>
            {tr('bulkIssueAction')}
          </button>
          <ul>
            {unreported.students.map(group => (
              <li key={group.student}>
                <span className="receipts-pending-name">{displayName(group.student)}</span>
                <span className="receipts-pending-meta">
                  <bdi>{group.count}</bdi> · <MoneyText amount={group.total} />
                </span>
                <button type="button" className="primary-button" onClick={() => void consolidate(group.student)}>
                  {tr('writeReceipt')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="receipts-filters" aria-label={tr('search')}>
        <input
          type="search"
          className="text-input"
          value={filters.q}
          onChange={event => set('q', event.target.value)}
          placeholder={tr('receiptSearchPlaceholder')}
          aria-label={tr('receiptSearchPlaceholder')}
        />
        <select className="text-input" value={filters.status} onChange={event => set('status', event.target.value)} aria-label={tr('status')}>
          <option value="">{tr('allStatuses')}</option>
          <option value="draft">{tr('receiptDraft')}</option>
          <option value="issued">{tr('receiptIssued')}</option>
          <option value="cancelled">{tr('receiptCancelledShort')}</option>
        </select>
        <select className="text-input" value={filters.method} onChange={event => set('method', event.target.value)} aria-label={tr('paymentMethod')}>
          <option value="">{tr('allMethods')}</option>
          {PAYMENT_METHODS.map(method => (
            <option key={method} value={method}>{tr(METHOD_KEYS[method])}</option>
          ))}
        </select>
        {/* The four range fields carry visible labels. A date input renders as
            a bare mm/dd/yyyy placeholder, and two of them side by side are
            indistinguishable without being told which end is which. */}
        <label className="field">
          <span>{tr('fromDate')}</span>
          <input className="text-input" type="date" value={filters.from} onChange={event => set('from', event.target.value)} />
        </label>
        <label className="field">
          <span>{tr('toDate')}</span>
          <input className="text-input" type="date" value={filters.to} onChange={event => set('to', event.target.value)} />
        </label>
        <label className="field">
          <span>{tr('minAmount')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={filters.minAmount}
            onChange={event => set('minAmount', event.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="field">
          <span>{tr('maxAmount')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={filters.maxAmount}
            onChange={event => set('maxAmount', event.target.value.replace(/\D/g, ''))}
          />
        </label>
        <button type="button" className="ghost-button receipts-reset" onClick={() => setFilters(NO_FILTERS)} disabled={!filtering}>
          {tr('resetFilters')}
        </button>
      </section>

      <section className="receipts-actions">
        <span className="receipts-count">{tr('receiptsShowing', visible.length)}</span>
        <button type="button" className="ghost-button" onClick={() => window.print()}>{tr('printList')}</button>
        <button type="button" className="ghost-button" onClick={() => exportCsv(rowsForExport(), `receipts-${exportStamp()}`)} disabled={visible.length === 0}>CSV</button>
        <button type="button" className="ghost-button" onClick={() => exportXls(rowsForExport(), `receipts-${exportStamp()}`)} disabled={visible.length === 0}>XLS</button>
        {/* JSON exports the receipts as served, not the flattened rows: it is
            the format that is meant to survive a round trip. */}
        <button type="button" className="ghost-button" onClick={() => exportJson(visible, `receipts-${exportStamp()}`)} disabled={visible.length === 0}>JSON</button>
      </section>

      {visible.length === 0
        ? filtering
          ? <EmptyState title={tr('noReceiptsMatch')} hint={tr('noReceiptsMatchHint')} />
          : <EmptyState title={tr('noReceipts')} hint={tr('noReceiptsHint')} />
        : (
          <ul className="receipt-rows">
            {visible.map(receipt => (
              <li key={receipt.id} className={`receipt-row is-${receipt.status}`}>
                <button type="button" className="receipt-row-button" onClick={() => void openReceipt(receipt.id)}>
                  <span className="receipt-row-main">
                    <span className="receipt-row-number">
                      {receipt.receiptNumber
                        ? <bdi>{receipt.receiptNumber}</bdi>
                        : tr('receiptDraft')}
                    </span>
                    <span className="receipt-row-customer">{partyDisplayName(receipt.customerName ?? receipt.student ?? '', students, language)}</span>
                  </span>
                  <MoneyText amount={receipt.amount} className="receipt-row-amount" />
                  <span className="receipt-row-date">
                    {receipt.issueDate && <bdi>{formatDate(receipt.issueDate, language)}</bdi>}
                  </span>
                </button>
                <span className="receipt-row-actions">
                  {/* Editing and deleting are for drafts only. An issued
                      receipt has a serial behind it, and the numbering exists
                      precisely so that it cannot be changed afterwards. */}
                  {receipt.status === 'draft' && (
                    <>
                      <button type="button" className="ghost-button" onClick={() => void editDraft(receipt.id)}>{tr('edit')}</button>
                      <button type="button" className="ghost-button is-destructive" onClick={() => setDeleting(receipt)}>{tr('delete')}</button>
                    </>
                  )}
                  <button type="button" className="ghost-button" onClick={() => void duplicateReceipt(receipt.id)}>{tr('duplicate')}</button>
                  {receipt.status === 'issued' && (
                    <button type="button" className="ghost-button is-destructive" onClick={() => setCancelling(receipt)}>
                      {tr('cancelReceipt')}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

      {open && (
        <ReceiptDocument
          receipt={open}
          onClose={() => setOpen(null)}
          onChanged={() => { void reload(); }}
        />
      )}

      {editing && (
        <ReceiptEditor
          receipt={editing.receipt}
          duplicate={editing.duplicate}
          onClose={() => setEditing(null)}
          onSaved={saved => {
            setEditing(null);
            void reload();
            setOpen(saved);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={tr('deleteDraftTitle')}
          // A draft has no serial, so deleting one costs nothing but the work.
          body={tr('deleteDraftBody')}
          confirmLabel={tr('delete')}
          destructive
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            const id = deleting.id;
            setDeleting(null);
            ReceiptService.deleteDraft(id).then(() => void reload()).catch(() => setError('SERVER_ERROR'));
          }}
        />
      )}

      {cancelling && (
        <CancelReceiptDialog
          receipt={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => { setCancelling(null); void reload(); }}
        />
      )}

      {bulkConfirming && unreported && (
        <ConfirmDialog
          title={tr('bulkIssueTitle', unreported.count)}
          body={tr('bulkIssueBody')}
          confirmLabel={bulkIssuing ? tr('loading') : tr('bulkIssueConfirm')}
          onCancel={() => setBulkConfirming(false)}
          onConfirm={() => void bulkIssue()}
        />
      )}
    </section>
  );

  // The list is served without items, so the full receipt is fetched when one
  // is opened rather than carrying every line for every row.
  async function openReceipt(id: number) {
    try {
      setOpen(await ReceiptService.get(id));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function editDraft(id: number) {
    try {
      setEditing({ receipt: await ReceiptService.get(id), duplicate: false });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function duplicateReceipt(id: number) {
    try {
      setEditing({ receipt: await ReceiptService.get(id), duplicate: true });
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }
}

function CancelReceiptDialog({
  receipt, onClose, onDone
}: { receipt: ReceiptModel; onClose: () => void; onDone: () => void }) {
  const { tr } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <ConfirmDialog
      title={tr('cancelReceiptTitle', receipt.receiptNumber ?? '')}
      // A cancellation is permanent and the number is not returned to the pool.
      body={tr('cancelReceiptBody')}
      confirmLabel={busy ? tr('loading') : tr('cancelReceipt')}
      destructive
      onCancel={onClose}
      onConfirm={() => {
        if (!reason.trim()) return;
        setBusy(true);
        ReceiptService.cancel(receipt.id, reason.trim()).then(onDone).catch(() => setBusy(false));
      }}
    >
      <label className="field is-wide">
        <span>{tr('cancelReceiptReason')}</span>
        <input
          className="text-input"
          value={reason}
          onChange={event => setReason(event.target.value)}
          required
        />
      </label>
    </ConfirmDialog>
  );
}
